import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import AIChatWidget from '../components/AIChatWidget';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  .ml-root { font-family: 'Inter', sans-serif; background: #fff; color: #0f172a; -webkit-font-smoothing: antialiased; min-height: 100vh; }
  @keyframes slideIn { from { opacity:0; transform:translateX(100%); } to { opacity:1; transform:translateX(0); } }
  @keyframes dropDown { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }

  .ml-nav { position:sticky; top:0; z-index:200; background:rgba(255,255,255,0.97); backdrop-filter:blur(16px); border-bottom:1px solid #f1f5f9; box-shadow:0 1px 12px rgba(0,0,0,0.06); }
  .ml-nav-inner { max-width:1200px; margin:0 auto; display:flex; align-items:center; height:64px; padding:0 24px; gap:4px; }
  .ml-nav-a { color:#475569; text-decoration:none; font-size:14px; font-weight:500; padding:6px 12px; border-radius:8px; transition:all .2s; white-space:nowrap; }
  .ml-nav-a:hover, .ml-nav-a.active { color:#0f172a; background:#f1f5f9; }

  /* Dropdown */
  .ml-dropdown { position:relative; }
  .ml-dropdown-btn { display:flex; align-items:center; gap:4px; color:#475569; font-size:14px; font-weight:500; padding:6px 12px; border-radius:8px; cursor:pointer; background:none; border:none; font-family:Inter,sans-serif; transition:all .2s; }
  .ml-dropdown-btn:hover { color:#0f172a; background:#f1f5f9; }
  .ml-dropdown-btn.open { color:#0f172a; background:#f1f5f9; }
  .ml-dropdown-arrow { font-size:10px; transition:transform .2s; }
  .ml-dropdown-arrow.open { transform:rotate(180deg); }
  .ml-dropdown-menu { position:absolute; top:100%; left:50%; margin-top:8px; transform:translateX(-50%); background:#fff; border:1px solid #e2e8f0; border-radius:16px; padding:8px; box-shadow:0 16px 48px rgba(0,0,0,0.12); width:560px; display:grid; grid-template-columns:1fr 1fr; gap:2px; animation:dropDown .15s ease; z-index:300; }
  .ml-dropdown-menu::before { content:''; position:absolute; left:0; right:0; top:-10px; height:10px; }
  .ml-dropdown-item { display:flex; align-items:flex-start; gap:12px; padding:12px 14px; border-radius:10px; text-decoration:none; transition:background .15s; }
  .ml-dropdown-item:hover { background:#f8fafc; }
  .ml-dropdown-icon { width:36px; height:36px; border-radius:9px; display:flex; align-items:center; justify-content:center; font-size:17px; flex-shrink:0; }
  .ml-dropdown-label { font-size:14px; font-weight:600; color:#0f172a; margin-bottom:2px; }
  .ml-dropdown-desc { font-size:12px; color:#64748b; line-height:1.5; }

  /* Mobile */
  .ml-hamburger { display:none; flex-direction:column; gap:5px; cursor:pointer; background:none; border:none; padding:8px; border-radius:8px; }
  .ml-hamburger span { width:22px; height:2px; background:#475569; border-radius:2px; display:block; }
  .ml-mobile-menu { display:none; position:fixed; inset:0; z-index:999; background:#fff; padding:76px 24px 32px; flex-direction:column; overflow-y:auto; animation:slideIn .25s ease; }
  .ml-mobile-menu.open { display:flex; }
  .ml-mobile-section { font-size:11px; font-weight:700; color:#94a3b8; letter-spacing:1px; text-transform:uppercase; padding:16px 0 8px; }
  .ml-mobile-a { color:#0f172a; text-decoration:none; font-size:16px; font-weight:500; padding:11px 0; border-bottom:1px solid #f8fafc; display:flex; align-items:center; gap:10px; }

  /* Footer */
  .ml-footer { background:#0f172a; padding:64px 24px 32px; }
  .ml-footer-grid { max-width:1200px; margin:0 auto; display:grid; grid-template-columns:2fr 1fr 1fr 1fr; gap:48px; margin-bottom:48px; }
  .ml-footer-a { display:block; margin-bottom:10px; color:#64748b; text-decoration:none; font-size:14px; transition:color .2s; }
  .ml-footer-a:hover { color:#fff; }
  .ml-footer-label { font-size:11px; font-weight:700; color:#475569; letter-spacing:1px; text-transform:uppercase; margin-bottom:16px; }
  .ml-footer-bottom { max-width:1200px; margin:0 auto; border-top:1px solid #1e293b; padding-top:24px; display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:12px; }

  @media (max-width:768px) {
    .ml-nav-links { display:none !important; }
    .ml-hamburger { display:flex !important; }
    .ml-footer-grid { grid-template-columns:1fr 1fr !important; gap:32px !important; }
    .ml-dropdown-menu { width:calc(100vw - 48px); left:0; transform:none; }
  }
  @media (max-width:480px) {
    .ml-footer-grid { grid-template-columns:1fr !important; }
  }
`;

const LOGO = ({ dark = false }) => (
  <a href="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 9 }}>
    <div style={{ width: 34, height: 34, borderRadius: 10, background: 'linear-gradient(135deg,#2563eb,#7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: '#fff', fontWeight: 800 }}>⊛</div>
    <span style={{ fontFamily: 'Inter,sans-serif', fontWeight: 800, fontSize: 17, color: dark ? '#fff' : '#0f172a', letterSpacing: '-0.4px' }}>ClickSaver</span>
  </a>
);

const PRODUCTS = [
  { to: '/product/total-protection', icon: '◉', bg: '#eff6ff', label: 'Total Protection Suite', desc: 'All-in-one shield across every paid and organic channel.' },
  { to: '/product/ppc-shield',       icon: '⛉', bg: '#f0fdf4', label: 'PPC Shield',             desc: 'Real-time click-fraud blocking for Google Ads, Meta & Microsoft.' },
  { to: '/product/bot-mitigation',   icon: '◍', bg: '#fef3c7', label: 'Bot Mitigation',         desc: 'Stop scrapers, crawlers and automation beyond the click.' },
  { to: '/product/lead-shield',      icon: '⤶', bg: '#fdf2f8', label: 'Lead Shield',            desc: 'Score every form submission and block fake leads before they hit your CRM.' },
];

const RESOURCES = [
  { to: '/about',            icon: 'ℹ️', bg: '#f0f9ff', label: 'About Us',         desc: 'Our mission and how ClickSaver works.' },
  { to: '/blog',             icon: '📝', bg: '#eff6ff', label: 'Blog',             desc: 'Articles and guides on click fraud and PPC.' },
  { to: '/case-studies',     icon: '📊', bg: '#f0fdf4', label: 'Case Studies',     desc: 'Real results from real ClickSaver customers.' },
  { to: '/faq',              icon: '💬', bg: '#f5f3ff', label: 'FAQ',              desc: 'Answers to the most common questions.' },
  { to: '/knowledge-center', icon: '📚', bg: '#fff7ed', label: 'Knowledge Center', desc: 'Setup guides, docs, and tutorials.' },
  { to: '/press',            icon: '📰', bg: '#fef2f2', label: 'Press',            desc: 'ClickSaver in the news and media.' },
  { to: '/research',         icon: '🔬', bg: '#ecfeff', label: 'Research',         desc: 'Data and insights on ad fraud trends.' },
  { to: '/reviews',          icon: '⭐', bg: '#fffbeb', label: 'Reviews',          desc: 'What customers say about ClickSaver.' },
  { to: '/why-us',           icon: '🏆', bg: '#f0fdf4', label: 'Why Us?',          desc: 'How ClickSaver compares to alternatives.' },
];

export default function MarketingLayout({ children, hero }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [productOpen, setProductOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => setLoggedIn(!!u));
    return unsub;
  }, []);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    setProductOpen(false);
    setResourcesOpen(false);
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // Close dropdown on outside click
  useEffect(() => {
    const fn = (e) => {
      if (!e.target.closest('.ml-dropdown-product')) setProductOpen(false);
      if (!e.target.closest('.ml-dropdown-resources')) setResourcesOpen(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  const ratingSchema = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "ClickSaver",
    "url": "https://clicksaver.app",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "148",
      "bestRating": "5",
      "worstRating": "1"
    }
  });

  return (
    <div className="ml-root">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: ratingSchema }} />
      <style>{CSS}</style>

      {/* Mobile menu */}
      <div className={`ml-mobile-menu ${menuOpen ? 'open' : ''}`}>
        <button onClick={() => setMenuOpen(false)} style={{ position:'absolute', top:18, right:18, background:'#f1f5f9', border:'none', borderRadius:8, width:36, height:36, fontSize:20, cursor:'pointer', color:'#475569', display:'flex', alignItems:'center', justifyContent:'center' }}>✕</button>
        <Link to="/" className="ml-mobile-a" style={{ fontSize:18, fontWeight:700 }}><span>🏠</span>Home</Link>
        <Link to="/pricing" className="ml-mobile-a"><span>💳</span>Pricing</Link>
        <Link to="/agencies" className="ml-mobile-a"><span>🤝</span>Agencies</Link>
        <div className="ml-mobile-section">Resources</div>
        {RESOURCES.map(r => (
          <Link key={r.to} to={r.to} className="ml-mobile-a">
            <span style={{ width:28, height:28, borderRadius:7, background:r.bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:14 }}>{r.icon}</span>
            {r.label}
          </Link>
        ))}
        <div style={{ marginTop:20, display:'flex', flexDirection:'column', gap:10 }}>
          {loggedIn ? (
            <Link to="/dashboard" onClick={() => setMenuOpen(false)} style={{ display:'block', textAlign:'center', padding:'13px', borderRadius:10, fontSize:15, fontWeight:600, textDecoration:'none', background:'#2563eb', color:'#fff' }}>Go to Dashboard</Link>
          ) : (
            <>
              <Link to="/login" onClick={() => setMenuOpen(false)} style={{ display:'block', textAlign:'center', padding:'13px', borderRadius:10, fontSize:15, fontWeight:600, textDecoration:'none', border:'1.5px solid #e2e8f0', color:'#374151' }}>Sign In</Link>
              <Link to="/register" onClick={() => setMenuOpen(false)} style={{ display:'block', textAlign:'center', padding:'13px', borderRadius:10, fontSize:15, fontWeight:600, textDecoration:'none', background:'#2563eb', color:'#fff' }}>Start Free Trial</Link>
            </>
          )}
        </div>
      </div>

      {/* Navbar */}
      <nav className={`ml-nav`} style={{ boxShadow: scrolled ? '0 1px 20px rgba(0,0,0,0.08)' : '0 1px 0 #f1f5f9' }}>
        <div className="ml-nav-inner">
          <LOGO />
          <div className="ml-nav-links" style={{ display:'flex', alignItems:'center', gap:2, marginLeft:28 }}>
            {/* Product dropdown */}
            <div
              className="ml-dropdown ml-dropdown-product"
              onMouseEnter={() => setProductOpen(true)}
              onMouseLeave={() => setProductOpen(false)}
            >
              <button className={`ml-dropdown-btn ${productOpen ? 'open' : ''}`} onClick={() => setProductOpen(o => !o)}>
                Product
                <span className={`ml-dropdown-arrow ${productOpen ? 'open' : ''}`}>▾</span>
              </button>
              {productOpen && (
                <div className="ml-dropdown-menu">
                  {PRODUCTS.map(p => (
                    <Link key={p.label} to={p.to} className="ml-dropdown-item">
                      <div className="ml-dropdown-icon" style={{ background: p.bg }}>{p.icon}</div>
                      <div>
                        <div className="ml-dropdown-label">{p.label}</div>
                        <div className="ml-dropdown-desc">{p.desc}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link to="/pricing" className={`ml-nav-a ${location.pathname === '/pricing' ? 'active' : ''}`}>Pricing</Link>
            <Link to="/agencies" className={`ml-nav-a ${location.pathname === '/agencies' ? 'active' : ''}`}>Agencies</Link>

            {/* Resources dropdown */}
            <div
              className="ml-dropdown ml-dropdown-resources"
              onMouseEnter={() => setResourcesOpen(true)}
              onMouseLeave={() => setResourcesOpen(false)}
            >
              <button className={`ml-dropdown-btn ${resourcesOpen ? 'open' : ''}`} onClick={() => setResourcesOpen(o => !o)}>
                Resources
                <span className={`ml-dropdown-arrow ${resourcesOpen ? 'open' : ''}`}>▾</span>
              </button>
              {resourcesOpen && (
                <div className="ml-dropdown-menu">
                  {RESOURCES.map(r => (
                    <Link key={r.to} to={r.to} className="ml-dropdown-item">
                      <div className="ml-dropdown-icon" style={{ background: r.bg }}>{r.icon}</div>
                      <div>
                        <div className="ml-dropdown-label">{r.label}</div>
                        <div className="ml-dropdown-desc">{r.desc}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:8 }}>
            {loggedIn ? (
              <Link to="/dashboard" style={{ background:'#2563eb', color:'#fff', textDecoration:'none', padding:'9px 18px', borderRadius:8, fontSize:14, fontWeight:600, boxShadow:'0 2px 8px rgba(37,99,235,.3)' }}>Go to Dashboard</Link>
            ) : (
              <>
                <Link to="/login" style={{ color:'#475569', textDecoration:'none', fontSize:14, fontWeight:500, padding:'6px 12px', borderRadius:8, transition:'all .2s' }}
                  onMouseEnter={e => { e.target.style.background='#f1f5f9'; e.target.style.color='#0f172a'; }}
                  onMouseLeave={e => { e.target.style.background='transparent'; e.target.style.color='#475569'; }}>Sign In</Link>
                <Link to="/register" style={{ background:'#2563eb', color:'#fff', textDecoration:'none', padding:'9px 18px', borderRadius:8, fontSize:14, fontWeight:600, boxShadow:'0 2px 8px rgba(37,99,235,.3)' }}>Start Free Trial</Link>
              </>
            )}
            <button className="ml-hamburger" onClick={() => setMenuOpen(true)}><span /><span /><span /></button>
          </div>
        </div>
      </nav>

      {/* Page content */}
      {children}

      {/* Chat widget */}
      <AIChatWidget />

      {/* Footer */}
      <footer className="ml-footer">
        <div className="ml-footer-grid">
          <div>
            <LOGO dark />
            <p style={{ fontSize:13, color:'#64748b', lineHeight:1.7, marginTop:14, maxWidth:260 }}>
              Real-time click fraud protection for Google Ads advertisers. Stop paying for fake clicks.
            </p>
          </div>
          <div>
            <div className="ml-footer-label">Product</div>
            <a href="/#features" className="ml-footer-a">Features</a>
            <a href="/#how-it-works" className="ml-footer-a">How It Works</a>
            <Link to="/pricing" className="ml-footer-a">Pricing</Link>
            <Link to="/why-us" className="ml-footer-a">Why ClickSaver?</Link>
          </div>
          <div>
            <div className="ml-footer-label">Resources</div>
            <Link to="/blog" className="ml-footer-a">Blog</Link>
            <Link to="/case-studies" className="ml-footer-a">Case Studies</Link>
            <Link to="/faq" className="ml-footer-a">FAQ</Link>
            <Link to="/knowledge-center" className="ml-footer-a">Knowledge Center</Link>
            <Link to="/reviews" className="ml-footer-a">Reviews</Link>
            <Link to="/research" className="ml-footer-a">Research</Link>
            <Link to="/support" className="ml-footer-a">Support</Link>
            <Link to="/contact" className="ml-footer-a">Contact</Link>
          </div>
          <div>
            <div className="ml-footer-label">Company</div>
            <Link to="/about" className="ml-footer-a">About Us</Link>
            <Link to="/blog" className="ml-footer-a">Blog</Link>
            <Link to="/press" className="ml-footer-a">Press</Link>
            <Link to="/contact" className="ml-footer-a">Contact</Link>
            <Link to="/support" className="ml-footer-a">Support</Link>
            <a href="https://www.youtube.com/@ClickSaver" target="_blank" rel="noopener noreferrer" className="ml-footer-a" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <svg width="16" height="11" viewBox="0 0 24 17" fill="#dc2626"><path d="M23.5 2.6a3 3 0 0 0-2.1-2.1C19.5 0 12 0 12 0S4.5 0 2.6.5A3 3 0 0 0 .5 2.6C0 4.5 0 8.5 0 8.5s0 4 .5 5.9a3 3 0 0 0 2.1 2.1C4.5 17 12 17 12 17s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1c.5-1.9.5-5.9.5-5.9s0-4-.5-5.9z"/><path d="M9.5 12.1V4.9l6.3 3.6-6.3 3.6z" fill="#fff"/></svg>
              YouTube
            </a>
            <div className="ml-footer-label" style={{ marginTop: 16 }}>Legal</div>
            <Link to="/privacy-policy" className="ml-footer-a">Privacy Policy</Link>
            <Link to="/terms-policy" className="ml-footer-a">Terms of Service</Link>
            <Link to="/cookie-policy" className="ml-footer-a">Cookie Policy</Link>
            <Link to="/gdpr" className="ml-footer-a">GDPR</Link>
          </div>
        </div>
        <div className="ml-footer-bottom">
          <span style={{ fontSize:13, color:'#334155' }}>© {new Date().getFullYear()} ClickSaver. All rights reserved.</span>
          <div style={{ display:'flex', gap:16, alignItems:'center' }}>
            <span style={{ fontSize:12, color:'#334155' }}>🔒 Secured by Stripe</span>
            <span style={{ fontSize:12, color:'#334155' }}>· GDPR Compliant</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
